import "keen-slider/keen-slider.min.css"
import React, {
    useEffect,
    useState,
    useMemo,
    useRef,
    useLayoutEffect,
    useCallback,
    use,
} from "react"
import { useRouter } from "next/router"
import AppBanner from "components/Sections/AppsSection/AppBanner"
import HeroVTwo from "components/HeroVTwo"
import { MainLayout } from "components/Layout"
import AdSection from "components/Sections/AdSection/AdSection"
import EventsSections from "components/Sections/EventsSections/EventsSections"
import { AdvertisementResponse } from "types/Advertisements"
import { useKeenSlider, KeenSliderOptions } from "keen-slider/react"
import { IFinalEvent } from "types/event"
import {
    filterEventsByDate,
    FilterOption,
} from "utils/helpers/filterEventsByDate"
import EventFilteration from "components/atoms/EventFiltration/eventFiltration.index"
import EventFilteratioMobile from "components/atoms/EventFiltration/eventFiltration.mobile"
import { NextSeo, SoftwareAppJsonLd, OrganizationJsonLd } from "next-seo"
import { ArSEO, EnSEO } from "../seo/index"
import { useQuery, QueryClient, dehydrate } from "@tanstack/react-query"
import getSections from "api/sections/getSections"
import FeatureFlagWrapper from "components/FeatureFlags/FeatureFlagWrapper"
import AdsSlider from "./ads"
import { useLang } from "hooks/useLang"
import useDevice from "hooks/useDevice"
import useWindowSize from "hooks/useWindowSize"
import {
    GOOGLE_PLAY_STORE_URL,
    APPLE_APP_STORE_URL,
} from "utils/helpers/constants"

// Sections
// import VenuesSection from "components/Sections/VenuesSection/venusesSection"
// import ArtistsSection from "components/Sections/ArtistsSection/artistsSection"
import FunFactSection from "components/Sections/funFacts/funFactsSection"
import AuthSection from "components/Sections/AuthSection/authSection"
import WhyBookOnPassTicketsSection from "components/Sections/whyBookOnPassTicketsSection/whyBookOnPassTicketsSection"
import CitiesSection from "components/Sections/citiesSection/citiesSection"
import SelectedArticles from "components/Sections/selectedArticles/selectedArticles"
import DiscoverWonderSection from "components/Sections/discoverPas/discoverWonderSection"
import NeverMissOutSection from "components/Sections/neverMissOutSection/neverMissOutSection"
import SpecialSectionOneCard from "components/Sections/specialSection/specialSectionOneEvent"
import HeroAdsStandalone from "components/heroAloneStandalone/heroAlone"
import HomePageSkeleton from "components/homeSkelton/homeSkelton"
import { useUserContext } from "context/UserContext"
import { ISections } from "types/ISections"

import { COMPANY_INFO } from "../constants/company_info"

export interface IPromo {
    eventTitle: string
    status: boolean
    id: string
}

interface PageProps {
    ads: AdvertisementResponse["payload"]
    dehydratedState?: unknown
}

export async function getServerSideProps() {
    const queryClient = new QueryClient()

    let ads: AdvertisementResponse["payload"] = []

    try {
        const [adsRes] = await Promise.all([
            fetch(`${process.env.NEXT_PUBLIC_API_URL}advertisement-groups`),
            queryClient.prefetchQuery({
                queryKey: ["sections"],
                queryFn: getSections,
            }),
        ])

        if (!adsRes.ok) {
            throw new Error(`HTTP error! status: ${adsRes.status}`)
        }

        const contentType = adsRes.headers.get("content-type")
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error("Oops, we haven't got JSON!")
        }

        const data = (await adsRes.json()) as AdvertisementResponse
        ads = data.payload
    } catch (error) {
        console.error("Error fetching home page data:", error)
    }

    return {
        props: {
            ads,
            dehydratedState: dehydrate(queryClient),
        },
    }
}

const HomePage = ({ ads: advertisement }: PageProps) => {
    const router = useRouter()
    const { isAr } = useLang()
    const { osType } = useDevice()
    const [currentSlide, setCurrentSlide] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const heroRef = useRef<HTMLDivElement | null>(null)
    const { user, token } = useUserContext()

    // Ads filtering
    const advertisementSliders = useMemo(
        () =>
            advertisement?.filter((ad) => ad.web_view_type === "slider") || [],
        [advertisement]
    )
    const advertisementGrid = useMemo(
        () => advertisement.filter((ad) => ad.web_view_type === "slider"),
        [advertisement]
    )

    // Sections query
    const { data: sectionsDataResponse, isLoading } = useQuery({
        queryKey: ["sections"],
        queryFn: getSections,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    })
    const sectionsData = sectionsDataResponse?.payload.sections || []

    // All events
    const allEvents = useMemo(
        () =>
            sectionsData?.flatMap(
                (section) => section.events || section.videos
            ) || [],
        [sectionsData]
    )
    const [filteredEvents, setFilteredEvents] =
        useState<IFinalEvent[]>(allEvents)

    useEffect(() => {
        if (allEvents.length > 0) setFilteredEvents(allEvents)
    }, [allEvents])

    const sliderOptions = useMemo<KeenSliderOptions>(() => {
        const canLoop = advertisementGrid.length > 1

        return {
            initial: 0,
            loop: canLoop,
            rtl: isAr,
            mode: "snap",
            renderMode: "performance",

            slides: {
                perView: 1,
                spacing: 0,
            },

            created() {
                setLoaded(true)
            },

            slideChanged(slider) {
                // 🛡️ HARD GUARD (fixes 'abs' crash)
                const details = slider.track?.details
                if (!details) return

                setCurrentSlide(details.rel)
            },
        }
    }, [isAr, advertisementGrid.length])

    const [sliderRef, instanceRef] = useKeenSlider(sliderOptions, [
        (slider) => {
            let timeout: ReturnType<typeof setTimeout> | undefined
            let mouseOver = false
            let destroyed = false

            const clearNextTimeout = () => {
                if (timeout) clearTimeout(timeout)
            }

            const nextTimeout = () => {
                clearNextTimeout()
                if (mouseOver || destroyed || advertisementGrid.length <= 1)
                    return

                timeout = setTimeout(() => {
                    // ✅ FINAL SAFETY GUARD (this fixes the crash)
                    if (destroyed || !slider.track?.details) return
                    slider.next()
                }, 4000)
            }

            const handleMouseOver = () => {
                mouseOver = true
                clearNextTimeout()
            }

            const handleMouseOut = () => {
                mouseOver = false
                nextTimeout()
            }

            slider.on("created", () => {
                slider.container.addEventListener("mouseover", handleMouseOver)
                slider.container.addEventListener("mouseout", handleMouseOut)
                nextTimeout()
            })

            slider.on("dragStarted", clearNextTimeout)
            slider.on("animationEnded", nextTimeout)
            slider.on("updated", nextTimeout)

            slider.on("destroyed", () => {
                destroyed = true
                clearNextTimeout()
            })

            return () => {
                destroyed = true
                clearNextTimeout()
                slider.container.removeEventListener(
                    "mouseover",
                    handleMouseOver
                )
                slider.container.removeEventListener("mouseout", handleMouseOut)
            }
        },
    ])

    const isLoggedIn = token

    //   const renderSectionByType = useCallback((section: ISections) => {
    //   switch (section.type) {
    //     case "artists":
    //       return (
    //         <div key={section._id}>
    //           <ArtistsSection data={section.artists!} isLoading={isLoading} />
    //         </div>
    //       );
    //     case "venues":
    //       return (
    //         <div key={section._id}>
    //           <VenuesSection data={section.venues!} isLoading={isLoading} />
    //         </div>
    //       );
    //     default:
    //       return null;
    //   }
    // }, [isLoading]);
    return (
        <>
            <HomePageSkeleton active={isLoading && !loaded} />
            {/* SEO */}
            <NextSeo
                {...(isAr ? ArSEO : EnSEO)}
                canonical={`${process.env.NEXT_PUBLIC_SITE_URL || ""}${router.asPath}`}
            />
            <OrganizationJsonLd
                useAppDir={false}
                type="Organization"
                name="PassTickets"
                url={
                    process.env.NEXT_PUBLIC_SITE_URL ||
                    "https://pass-tickets.com"
                }
            />
            <SoftwareAppJsonLd
                useAppDir={false}
                type="MobileApplication"
                name="PassTickets"
                price="0"
                priceCurrency="USD"
                applicationCategory="EntertainmentApplication"
                operatingSystem="Android"
                offers={[
                    {
                        "@type": "Offer",
                        price: "0",
                        priceCurrency: "USD",
                        url: GOOGLE_PLAY_STORE_URL,
                    },
                ]}
            />
            <SoftwareAppJsonLd
                useAppDir={false}
                type="MobileApplication"
                name="PassTickets"
                price="0"
                priceCurrency="USD"
                applicationCategory="EntertainmentApplication"
                operatingSystem="iOS"
                offers={[
                    {
                        "@type": "Offer",
                        price: "0",
                        priceCurrency: "USD",
                        url: APPLE_APP_STORE_URL,
                    },
                ]}
            />

            {
                <MainLayout>
                    {/* Hero Section */}
                    <div
                        ref={heroRef}
                        className="fixed left-0 top-0 -z-10 md:aspect-video h-full md:max-h-screen w-full overflow-hidden md:block"
                    >
                        <HeroVTwo
                            sliderRef={sliderRef}
                            instanceRef={instanceRef}
                            advertisements={advertisementSliders}
                            currentSlide={currentSlide}
                            loaded={loaded}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex flex-col">
                        <AdsSlider
                            currentSlide={currentSlide}
                            loaded={loaded}
                            sliderRef={sliderRef}
                            instanceRef={instanceRef}
                            ads={advertisement}
                        />

                        {/* Events Sections */}
                        <EventsSections
                            sectionsData={sectionsData}
                            filteredEvents={filteredEvents}
                            isLoading={false}
                        />

                        {/* Never Miss Out */}
                        <NeverMissOutSection />

                        <FunFactSection isLoading={false} />
                        <HeroAdsStandalone ads={advertisement} />

                        {/* <SpecialSectionOneCard   img="dj" fromColor="#273C3C"/> */}
                        <DiscoverWonderSection isLoading={false} />
                        {/* <SelectedArticles isLoading={false} /> */}
                        <CitiesSection isLoading={false} />

                        {!isLoggedIn && <AuthSection isLoading={false} />}
                        <WhyBookOnPassTicketsSection isLoading={false} />

                        {/* Bottom Ads */}
                        {/* {advertisementGrid.length > 0 && (
            <AdSection advertisements={advertisementGrid} isLoading={false} />
          )} */}

                        {/* <AppBanner /> */}
                    </div>
                </MainLayout>
            }
        </>
    )
}

export default HomePage
